import type { Node } from '@xyflow/react';
import { getAvailableVariables } from '@/components/automation/variableUtils';


export interface TemplatePlaceholder {
  key: string;
  source: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTON';
  label: string;
}

const SOURCE_ORDER: TemplatePlaceholder['source'][] = ['HEADER', 'BODY', 'FOOTER', 'BUTTON'];

function collectTextsFromComponents(
  components: any[]
): Array<{ text: string; source: TemplatePlaceholder['source'] }> {
  if (!Array.isArray(components)) return [];
  const buckets: Record<TemplatePlaceholder['source'], string[]> = {
    HEADER: [],
    BODY: [],
    FOOTER: [],
    BUTTON: [],
  };

  for (const c of components) {
    const type = String(c?.type || '').toUpperCase();
    if (type === 'HEADER' && typeof c?.text === 'string') buckets.HEADER.push(c.text);
    if (type === 'BODY' && typeof c?.text === 'string') buckets.BODY.push(c.text);
    if (type === 'FOOTER' && typeof c?.text === 'string') buckets.FOOTER.push(c.text);
    if (type === 'BUTTONS' && Array.isArray(c?.buttons)) {
      for (const b of c.buttons) {
        const btnType = String(b?.type || '').toUpperCase();
        if (btnType === 'URL' && typeof b?.url === 'string') buckets.BUTTON.push(b.url);
      }
    }
  }

  const out: Array<{ text: string; source: TemplatePlaceholder['source'] }> = [];
  for (const source of SOURCE_ORDER) {
    for (const text of buckets[source]) out.push({ text, source });
  }
  return out;
}

export function detectTemplatePlaceholders(components: any[]): TemplatePlaceholder[] {
  const texts = collectTextsFromComponents(components);
  const seen = new Set<string>();
  const out: TemplatePlaceholder[] = [];

  const pattern = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  for (const { text, source } of texts) {
    let m: RegExpExecArray | null;
    pattern.lastIndex = 0;
    while ((m = pattern.exec(text)) !== null) {
      const key = m[1];
      if (seen.has(key)) continue;
      seen.add(key);
      const sourceLabel = source.charAt(0) + source.slice(1).toLowerCase();
      out.push({
        key,
        source,
        label: `${sourceLabel} — Variable {{${key}}}`,
      });
    }
  }
  return out;
}

export function renderTemplatePreview(bodyText: string, variables: Record<string, string>): string {
  if (!bodyText) return '';
  return bodyText.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key) => {
    const raw = variables?.[key];
    if (raw == null || raw === '') return match;
    return raw;
  });
}


export interface VariableSuggestion {
  value: string;
  label: string;
  category: 'contact' | 'system' | 'trigger';
}

const TRIGGER_TO_NODE_TYPE: Record<string, string> = {
  'order.paid': 'shopify_order_paid_trigger',
  'order.placed': 'shopify_order_created_trigger',
  'order.cancelled': 'shopify_order_cancelled_trigger',
  'order.updated': 'shopify_order_updated_trigger',
  'order.shipped': 'shopify_order_shipped_trigger',
  'order.out_for_delivery': 'shopify_order_out_for_delivery_trigger',
  'order.delivered': 'shopify_order_delivered_trigger',
  'checkout.updated': 'shopify_abandoned_cart_trigger',
  'customer.created': 'shopify_customer_created_trigger',
  'refund.created': 'shopify_refund_created_trigger',
};

const PLAYBOOK_TRIGGER_NODE_ID = 'playbook-trigger';

const EXCLUDED_VAR_PATHS = new Set([
  PLAYBOOK_TRIGGER_NODE_ID, // bare "(Formatted)" entry — just the whole payload
  'message',
  'phoneNumber',
  'shopify.access_token',
  'shopify.shop',
  'shopify.api_version',
  'petpooja.app_key',
  'petpooja.app_secret',
  'petpooja.access_token',
  'petpooja.base_url',
  'petpooja.restaurant_id',
]);

function cleanLabel(label: string, prefix: string): string {
  if (label.startsWith(prefix + ' ')) return label.substring(prefix.length + 1);
  return label;
}

export function getVariableSuggestions(triggerType: string): VariableSuggestion[] {
  const triggerNodeType = TRIGGER_TO_NODE_TYPE[triggerType] || 'shopify_trigger';
  const label = 'Trigger';

  const syntheticNodes: Node[] = [
    {
      id: PLAYBOOK_TRIGGER_NODE_ID,
      type: triggerNodeType,
      position: { x: 0, y: 0 },
      data: { label },
    } as unknown as Node,
  ];

  const vars = getAvailableVariables(syntheticNodes);

  const suggestions: VariableSuggestion[] = [];
  for (const v of vars) {
    if (EXCLUDED_VAR_PATHS.has(v.id)) continue;

    let category: VariableSuggestion['category'];
    if (v.category === 'contact') category = 'contact';
    else if (v.id.startsWith(PLAYBOOK_TRIGGER_NODE_ID + '.')) category = 'trigger';
    else category = 'system';

    suggestions.push({
      value: `{{${v.id}}}`,
      label: category === 'trigger' ? cleanLabel(v.label, label) : v.label,
      category,
    });
  }

  return suggestions;
}

export function groupSuggestions(
  suggestions: VariableSuggestion[]
): Array<{ title: string; items: VariableSuggestion[]; category: VariableSuggestion['category'] }> {
  const buckets: Record<VariableSuggestion['category'], VariableSuggestion[]> = {
    trigger: [],
    contact: [],
    system: [],
  };
  for (const s of suggestions) buckets[s.category].push(s);
  return [
    { title: 'From this event', items: buckets.trigger, category: 'trigger' as const },
    { title: 'Contact fields', items: buckets.contact, category: 'contact' as const },
    { title: 'System', items: buckets.system, category: 'system' as const },
  ].filter((g) => g.items.length > 0);
}

export function filterSuggestions(
  suggestions: VariableSuggestion[],
  query: string
): VariableSuggestion[] {
  const q = (query || '').trim().toLowerCase();
  if (!q) return suggestions;
  return suggestions.filter(
    (s) => s.label.toLowerCase().includes(q) || s.value.toLowerCase().includes(q)
  );
}
